import Dash from "components/DashboardContainer/Dash";
import { useAdminNavigationRedesignEnabled } from "components/Admin/hooks/useAdminNavigationRedesignEnabled";
import { FullScreenLoadingSpinner } from "components/FullScreenLoader";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import React from "react";
import { useLocation } from "react-router-dom";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";

import AuthenticatedAppShell from "../AuthenticatedAppShell";
import { ApplicationPageLayoutProvider } from "../ApplicationPageLayout";
import { ApplicationShellPreferenceProvider } from "../ApplicationShellPreference";

export type ApplicationShellMode = "loading" | "legacy" | "redesigned";
export const APPLICATION_SHELL_FLAG_RESOLUTION_TIMEOUT_MS = 750;

export const resolveApplicationShellMode = (
  redesignEnabled: boolean | undefined,
): ApplicationShellMode => {
  if (redesignEnabled === undefined) return "loading";
  return redesignEnabled ? "redesigned" : "legacy";
};

interface ResolveApplicationShellRedesignEnabledOptions {
  override: boolean | undefined;
  postHogValue: boolean | undefined;
}

export const resolveApplicationShellRedesignEnabled = ({
  override,
  postHogValue,
}: ResolveApplicationShellRedesignEnabledOptions): boolean | undefined => {
  if (override !== undefined) return override;
  return postHogValue;
};

const useResolvedApplicationShellMode = (
  redesignEnabled: boolean | undefined,
  identityReady: boolean,
  resolveImmediately: boolean,
  resolutionKey: string,
) => {
  const latestFlagValue = React.useRef(redesignEnabled);
  const [resolution, setResolution] = React.useState<{
    key: string;
    mode: ApplicationShellMode;
    complete: boolean;
  }>(() => ({
    key: resolutionKey,
    mode: resolveImmediately
      ? resolveApplicationShellMode(redesignEnabled)
      : "loading",
    complete: resolveImmediately,
  }));

  React.useEffect(() => {
    latestFlagValue.current = redesignEnabled;
  }, [redesignEnabled]);

  React.useEffect(() => {
    if (resolveImmediately) {
      setResolution({
        key: resolutionKey,
        mode: resolveApplicationShellMode(latestFlagValue.current),
        complete: true,
      });
      return;
    }

    setResolution({
      key: resolutionKey,
      mode: "loading",
      complete: false,
    });

    if (!identityReady) return;

    const timeout = window.setTimeout(() => {
      const resolvedMode = resolveApplicationShellMode(latestFlagValue.current);
      setResolution({
        key: resolutionKey,
        mode: resolvedMode === "loading" ? "legacy" : resolvedMode,
        complete: true,
      });
    }, APPLICATION_SHELL_FLAG_RESOLUTION_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [identityReady, resolutionKey, resolveImmediately]);

  if (resolveImmediately) {
    return resolveApplicationShellMode(redesignEnabled);
  }

  if (resolution.key !== resolutionKey || !identityReady) {
    return "loading";
  }

  if (resolution.complete) {
    const currentMode = resolveApplicationShellMode(redesignEnabled);
    return currentMode === "loading" ? "legacy" : currentMode;
  }

  return resolution.mode;
};

interface NonAdminApplicationShellProps {
  children: React.ReactNode;
  redesignEnabledOverride: boolean | undefined;
  postHogRedesignEligible: boolean | undefined;
  identityReady: boolean;
  resolutionKey: string;
}

const NonAdminApplicationShell: React.FC<NonAdminApplicationShellProps> = ({
  children,
  redesignEnabledOverride,
  postHogRedesignEligible,
  identityReady,
  resolutionKey,
}) => {
  const hasRedesignOverride = redesignEnabledOverride !== undefined;
  const resolvedRedesignEnabled = resolveApplicationShellRedesignEnabled({
    override: redesignEnabledOverride,
    postHogValue: postHogRedesignEligible,
  });
  const mode = useResolvedApplicationShellMode(
    resolvedRedesignEnabled,
    identityReady,
    hasRedesignOverride,
    resolutionKey,
  );

  if (mode === "loading") {
    return <FullScreenLoadingSpinner />;
  }

  if (mode === "redesigned") {
    return (
      <ApplicationPageLayoutProvider mode="fluid">
        <AuthenticatedAppShell>{children}</AuthenticatedAppShell>
      </ApplicationPageLayoutProvider>
    );
  }

  return (
    <ApplicationPageLayoutProvider mode="centered">
      <Dash>{children}</Dash>
    </ApplicationPageLayoutProvider>
  );
};

export interface AppShellBoundaryProps {
  children: React.ReactNode;
  /**
   * Test and Storybook override. Production callers should leave this unset so
   * PostHog remains the source of truth.
   */
  redesignEnabled?: boolean;
  identityReady?: boolean;
}

export const AppShellBoundary: React.FC<AppShellBoundaryProps> = ({
  children,
  redesignEnabled,
  identityReady: identityReadyOverride,
}) => {
  const location = useLocation();
  const adminNavigationRedesignEnabled = useAdminNavigationRedesignEnabled();
  const userContext = React.useContext(UserContext);
  const orgContext = React.useContext(OrgContext);
  const postHogRedesignEnabled = useIdentityFeatureFlagEnabled(
    POSTHOG_IDENTITY_FLAGS.APPLICATION_SHELL_REDESIGN,
  );
  const earlyAccessOptionEnabled = useIdentityFeatureFlagEnabled(
    POSTHOG_IDENTITY_FLAGS.APP_REDESIGN_EARLY_ACCESS_OPTION,
  );
  const hasRedesignOverride = redesignEnabled !== undefined;
  const identityReady =
    identityReadyOverride ??
    (hasRedesignOverride ||
      (!userContext.isLoading &&
        Boolean(userContext.me?.id) &&
        !orgContext.isLoading &&
        !orgContext.isOrgAccessLoading &&
        Boolean(orgContext.activeOrgId)));
  const isAdminRoute = /^\/admin(?:\/|$)/.test(location.pathname);

  if (isAdminRoute) {
    if (adminNavigationRedesignEnabled) {
      return (
        <ApplicationPageLayoutProvider mode="fluid">
          {children}
        </ApplicationPageLayoutProvider>
      );
    }

    return (
      <ApplicationPageLayoutProvider mode="centered">
        <Dash>{children}</Dash>
      </ApplicationPageLayoutProvider>
    );
  }

  return (
    <ApplicationShellPreferenceProvider
      canChoose={!hasRedesignOverride && earlyAccessOptionEnabled === true}
      redesignEnabled={postHogRedesignEnabled}
    >
      <NonAdminApplicationShell
        redesignEnabledOverride={redesignEnabled}
        postHogRedesignEligible={postHogRedesignEnabled}
        identityReady={identityReady}
        resolutionKey={`${userContext.me?.id ?? "no-user"}:${
          orgContext.activeOrgId || "no-org"
        }`}
      >
        {children}
      </NonAdminApplicationShell>
    </ApplicationShellPreferenceProvider>
  );
};

AppShellBoundary.displayName = "AppShellBoundary";

export default AppShellBoundary;
